import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Row, Col, Alert, Spinner, Badge, Modal } from 'react-bootstrap';

// URL base del backend
const API_BASE = process.env.REACT_APP_NODE_ENV === 'production' ? process.env.REACT_APP_API_URL_PRODUCTION : 'http://localhost:3001';

const RolesSection = ({ onSuccess }) => {
  // =====================================================
  // ESTADO
  // =====================================================
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [guardando, setGuardando] = useState(false);
  
  // Estado del formulario
  const [formData, setFormData] = useState({
    codigo: '',
    nombre: '',
    descripcion: '',
    color: '#6b7280'
  });

  // Colores predefinidos
  const coloresPredefinidos = [
    { nombre: 'Rojo', valor: '#dc2626' },
    { nombre: 'Naranja', valor: '#d97706' },
    { nombre: 'Verde', valor: '#059669' },
    { nombre: 'Azul', valor: '#2563eb' },
    { nombre: 'Púrpura', valor: '#7c3aed' },
    { nombre: 'Rosa', valor: '#db2777' },
    { nombre: 'Gris', valor: '#6b7280' },
    { nombre: 'Índigo', valor: '#4f46e5' }
  ];

  // =====================================================
  // CARGAR ROLES
  // =====================================================
  useEffect(() => {
    cargarRoles();
  }, []);

  const cargarRoles = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const correo = localStorage.getItem('correo');
      
      const response = await fetch(`${API_BASE}/api/config/roles`, {
        headers: {
          'Content-Type': 'application/json',
          'usuario-correo': correo || ''
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setRoles(data.data);
      } else {
        setError(data.error || 'Error al cargar roles');
      }
    } catch (err) {
      setError('Error de conexión');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // HANDLERS
  // =====================================================
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNuevoRol = () => {
    setFormData({
      codigo: '',
      nombre: '',
      descripcion: '',
      color: '#6b7280'
    });
    setEditando(null);
    setShowModal(true);
  };

  const handleEditarRol = (rol) => {
    setFormData({
      codigo: rol.codigo,
      nombre: rol.nombre,
      descripcion: rol.descripcion || '',
      color: rol.color || '#6b7280'
    });
    setEditando(rol._id);
    setShowModal(true);
  };

  const handleCerrarModal = () => {
    setShowModal(false);
    setEditando(null);
    setFormData({
      codigo: '',
      nombre: '',
      descripcion: '',
      color: '#6b7280'
    });
  };

  // =====================================================
  // GUARDAR ROL (CREAR O ACTUALIZAR)
  // =====================================================
  const handleGuardar = async (e) => {
    e.preventDefault();
    
    if (!formData.codigo.trim() || !formData.nombre.trim()) {
      setError('El código y nombre son obligatorios');
      return;
    }

    try {
      setGuardando(true);
      setError(null);

      const correo = localStorage.getItem('correo');
      const url = editando 
        ? `${API_BASE}/api/config/roles/${editando}`
        : `${API_BASE}/api/config/roles`;
      
      const method = editando ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'usuario-correo': correo || ''
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();

      if (data.success) {
        onSuccess?.(editando ? 'Rol actualizado correctamente' : 'Rol creado correctamente');
        handleCerrarModal();
        cargarRoles();
      } else {
        setError(data.error || 'Error al guardar');
      }
    } catch (err) {
      setError('Error de conexión al guardar');
      console.error('Error:', err);
    } finally {
      setGuardando(false);
    }
  };

  // =====================================================
  // ELIMINAR ROL
  // =====================================================
  const handleEliminar = async (rol) => {
    if (rol.esInmutable) {
      setError('Este rol no puede ser eliminado');
      return;
    }

    if (rol.cantidadUsuarios > 0) {
      setError(`No se puede eliminar "${rol.nombre}" porque tiene ${rol.cantidadUsuarios} usuario(s)`);
      return;
    }

    if (!window.confirm(`¿Estás seguro de eliminar el rol "${rol.nombre}"?`)) {
      return;
    }

    try {
      setError(null);
      const correo = localStorage.getItem('correo');
      
      const response = await fetch(`${API_BASE}/api/config/roles/${rol._id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'usuario-correo': correo || ''
        }
      });

      const data = await response.json();

      if (data.success) {
        onSuccess?.('Rol eliminado correctamente');
        cargarRoles();
      } else {
        setError(data.error || 'Error al eliminar');
      }
    } catch (err) {
      setError('Error de conexión al eliminar');
      console.error('Error:', err);
    }
  };

  // =====================================================
  // RENDER - LOADING
  // =====================================================
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Cargando roles...</span>
      </div>
    );
  }

  // =====================================================
  // RENDER PRINCIPAL
  // =====================================================
  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h5 className="mb-1">
            <i className="bi bi-people-fill text-primary me-2"></i>
            Gestión de Roles
          </h5>
          <small className="text-muted">Crea y administra los roles del sistema</small>
        </div>
        <Button variant="primary" onClick={handleNuevoRol}>
          <i className="bi bi-plus-lg me-2"></i>
          Nuevo Rol
        </Button>
      </div>

      {/* Mensaje de error */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)} className="mb-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {/* Lista de Roles */}
      <Row>
        {roles.map((rol) => (
          <Col md={6} lg={4} key={rol._id} className="mb-3">
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-start">
                  <div
                    className="rounded-circle me-3"
                    style={{ 
                      width: '12px', 
                      height: '12px', 
                      backgroundColor: rol.color || '#6b7280',
                      marginTop: '6px'
                    }}
                  />
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center mb-1">
                      <h6 className="mb-0 me-2">{rol.nombre}</h6>
                      <Badge bg="secondary" className="me-1">{rol.codigo}</Badge>
                      {rol.esInmutable && (
                        <Badge bg="warning" text="dark">
                          <i className="bi bi-lock-fill me-1"></i>
                          Protegido
                        </Badge>
                      )}
                    </div>
                    
                    {rol.descripcion && (
                      <p className="text-muted small mb-2">{rol.descripcion}</p>
                    )}
                    
                    <div className="d-flex gap-3 text-muted small">
                      <span>
                        <i className="bi bi-person me-1"></i>
                        {rol.cantidadUsuarios || 0} usuarios
                      </span>
                      <span>
                        <i className="bi bi-shield me-1"></i>
                        {rol.permisosActivos || 0} permisos
                      </span>
                    </div>
                  </div>
                </div>
              </Card.Body>
              <Card.Footer className="bg-transparent border-top-0">
                <div className="d-flex gap-2">
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => handleEditarRol(rol)}
                  >
                    <i className="bi bi-pencil me-1"></i>
                    Editar
                  </Button>
                  {!rol.esInmutable && (
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={() => handleEliminar(rol)}
                      disabled={rol.cantidadUsuarios > 0}
                      title={rol.cantidadUsuarios > 0 ? 'No se puede eliminar, tiene usuarios' : 'Eliminar'}
                    >
                      <i className="bi bi-trash me-1"></i>
                      Eliminar
                    </Button>
                  )}
                </div>
              </Card.Footer>
            </Card>
          </Col>
        ))}

        {roles.length === 0 && (
          <Col>
            <div className="text-center py-5 text-muted">
              <i className="bi bi-people fs-1 d-block mb-3"></i>
              <p>No hay roles configurados</p>
            </div>
          </Col>
        )}
      </Row>

      {/* Modal de Crear/Editar */}
      <Modal show={showModal} onHide={handleCerrarModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editando ? 'Editar Rol' : 'Nuevo Rol'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleGuardar}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Código *</Form.Label>
              <Form.Control
                type="text"
                name="codigo"
                value={formData.codigo}
                onChange={handleInputChange}
                disabled={editando}
                placeholder="ej: coordinador"
              />
              <Form.Text className="text-muted">
                Identificador único, sin espacios
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Nombre *</Form.Label>
              <Form.Control
                type="text"
                name="nombre"
                value={formData.nombre}
                onChange={handleInputChange}
                placeholder="ej: Coordinador"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="descripcion"
                value={formData.descripcion}
                onChange={handleInputChange}
                placeholder="Descripción de las funciones del rol..."
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Color</Form.Label>
              <div className="d-flex flex-wrap gap-2">
                {coloresPredefinidos.map((color) => (
                  <button
                    key={color.valor}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, color: color.valor }))}
                    className="btn p-0 rounded-circle"
                    style={{ 
                      width: '32px', 
                      height: '32px', 
                      backgroundColor: color.valor,
                      border: formData.color === color.valor ? '3px solid #000' : '2px solid #ddd'
                    }}
                    title={color.nombre}
                  />
                ))}
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCerrarModal}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={guardando}>
              {guardando ? (
                <>
                  <Spinner size="sm" animation="border" className="me-2" />
                  Guardando...
                </>
              ) : (
                <>
                  <i className="bi bi-check-lg me-2"></i>
                  Guardar
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default RolesSection;
